import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Form, Formik } from "formik";
import { motion } from "framer-motion";
import { axiosput } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { forgetpasswordrules } from "../../utils/validations";
import { Button } from "../../components/ui/Button";
import { FormInput } from "../../components/ui/FormInput";
import * as yup from "yup";

const resetPasswordRules = yup.object({
  security_answer: yup.string().required("Security Answer is required"),
  new_password: yup
    .string()
    .min(8, "Minimum of 8 characters expected")
    .max(32, "Maximum of 32 characters expected")
    .required("New Password is a required field"),
  confirmpassword: yup.string().oneOf([yup.ref("new_password"), null], "Passwords must match"),
});

const Forgot = () => {
  const navigate = useNavigate();
  const [step, setStep] = React.useState(1);
  const [securityQuestion, setSecurityQuestion] = React.useState("");
  const [emailAddress, setEmailAddress] = React.useState("");

  const handleEmailSubmit = async (values, { setSubmitting }) => {
    try {
      const email = values.emailaddress;
      const req = await axiosput("ms_applicant/forgot", {
        emailaddress: email,
      });
      if (req.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { type, message, question } = req.data;

      if (type === "question") {
        setSecurityQuestion(question);
        setEmailAddress(email);
        setStep(2);
      } else if (type === "error") {
        swal("error", message);
      } else {
        swal(type, message);
      }
    } catch ({ message }) {
      swal("warning", message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetSubmit = async (values, { setSubmitting }) => {
    try {
      const req = await axiosput("ms_applicant/forgot", {
        emailaddress: emailAddress,
        security_answer: values.security_answer,
        new_password: values.new_password
      });

      if (req.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }

      const { type, message } = req.data;
      if (type === "success") {
        swal("success", message);
        navigate("/");
      } else {
        swal("error", message);
      }
    } catch ({ message }) {
      swal("warning", message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - 40% - White with blue accent */}
      <div className="hidden lg:flex lg:w-[40%] relative bg-white overflow-hidden">
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Faint blue gradient glow behind logo */}
            <div className="relative mx-auto mb-5">
              <div className="absolute inset-0 -m-8 rounded-full bg-gradient-to-br from-primary-100 via-primary-50 to-transparent blur-2xl" />
              <img
                src="images/college-logo.png"
                alt="College Logo"
                className="h-28 mx-auto object-contain relative"
              />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-1">
              Adamu Tafawa Balewa
            </h1>
            <h2 className="text-base font-medium text-neutral-600 mb-0.5">
              College of Education, Kangere
            </h2>
            <p className="text-sm text-neutral-400">
              Bauchi State
            </p>

            <div className="mt-8 space-y-3">
              {[
                'Account Recovery',
                'Security Verification',
                'Quick Reset',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 justify-center">
                  <div className="w-6 h-6 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-sm text-neutral-600">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        {/* Subtle separator line */}
        <div className="absolute right-0 top-[15%] bottom-[15%] w-px bg-neutral-200" />
      </div>

      {/* Right Side - 60% - Form */}
      <div className="w-full lg:w-[60%] flex items-center justify-center px-6 sm:px-12 bg-neutral-50">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="mb-5 text-center">
            <h2 className="text-2xl font-bold text-neutral-900">
              {step === 1 ? "Find Your Account" : "Reset Password"}
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              {step === 1 ? "Enter your email to recover your account" : "Answer your security question and set a new password"}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-lg px-6 py-6 sm:px-8 sm:py-7">
            {step === 1 && (
              <Formik
                initialValues={{ emailaddress: "" }}
                validationSchema={forgetpasswordrules}
                onSubmit={handleEmailSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-4">
                    <FormInput
                      name="emailaddress"
                      label="Email Address"
                      type="email"
                      placeholder="Enter your registered email"
                      required
                      leftIcon={
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                      }
                    />

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={isSubmitting}
                    >
                      {isSubmitting ? "Searching..." : "Continue"}
                    </Button>
                  </Form>
                )}
              </Formik>
            )}

            {step === 2 && (
              <Formik
                initialValues={{ security_answer: "", new_password: "", confirmpassword: "" }}
                validationSchema={resetPasswordRules}
                onSubmit={handleResetSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-4">
                    <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                      <p className="text-xs text-primary-700 font-medium">Security Question:</p>
                      <p className="text-sm text-primary-900 mt-1 font-semibold">{securityQuestion}</p>
                    </div>

                    <FormInput
                      name="security_answer"
                      label="Security Answer"
                      placeholder="Enter your answer"
                      required
                      leftIcon={
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      }
                    />

                    <div className="grid grid-cols-2 gap-3">
                      <FormInput
                        name="new_password"
                        label="New Password"
                        type="password"
                        placeholder="New password"
                        required
                        leftIcon={
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        }
                      />
                      <FormInput
                        name="confirmpassword"
                        label="Confirm Password"
                        type="password"
                        placeholder="Confirm password"
                        required
                        leftIcon={
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        }
                      />
                    </div>

                    <div className="flex gap-3 pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        onClick={() => setStep(1)}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        fullWidth
                        loading={isSubmitting}
                      >
                        {isSubmitting ? "Resetting..." : "Reset Password"}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}

            <div className="flex items-center justify-center pt-4 mt-4 border-t border-neutral-100">
              <span className="text-sm text-neutral-500 mr-1.5">Remember your password?</span>
              <Link
                to="/"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>

          <p className="text-center text-xs text-neutral-400 mt-3">
            Need a new account?{" "}
            <Link to="/account" className="text-primary-600 hover:underline">Create Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Forgot;
